import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import {
  Divider,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { BarChartOutlined, FileDownloadOutlined, TableChartOutlined } from '@mui/icons-material';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { ControlProps, SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { MetricsDateControls } from './MetricsDateControls';
import {
  buildMetricRows,
  ResourceMetricsData,
  SortDirection,
  sortMetricRowsByDate,
} from '../metricRows';
import { MetricsTrendTable } from './MetricsTrendTable';
import { MetricsCombinedChart } from '../Charts/MetricsCombinedChart';
import { MetricsSeparateCharts } from '../SeparateCharts/MetricsSeparateCharts';
import { MetricsVolumeChart } from '../Charts/MetricsVolumeChart';
import { downloadMetricsCsv } from '../exportMetricsCsv';
import { MetricsDateRange } from '../dateRanges';
import styles from './MetricsTrendPanel.module.scss';

export type MetricsTrendView = 'chart' | 'table';

export type RecordsErrorsLayout = 'combined' | 'separate';

export type MetricView = 'recordsErrors' | 'volume';

export type MetricsTrendPanelProps = {
  data: ResourceMetricsData;
  loading?: boolean;
  start: string | Date;
  end: string | Date;
  onDatesChanged: (dates: MetricsDateRange) => void;
  hideRecords?: boolean;
  hideDateControls?: boolean;
  hideTitle?: boolean;
  title?: string;
  titleClassName?: string;
  resourceType?: string;
  resourceId?: string | number;
  csvCategory?: string;
};

const layoutControls: ControlProps[] = [
  { label: 'Combined', value: 'combined' },
  { label: 'Separate', value: 'separate' },
];

const metricControls: ControlProps[] = [
  { label: 'Records & Errors', value: 'recordsErrors' },
  { label: 'Volume', value: 'volume' },
];

export const MetricsTrendPanel = (props: MetricsTrendPanelProps) => {
  const {
    data,
    loading = false,
    start,
    end,
    onDatesChanged,
    hideRecords = false,
    hideDateControls = false,
    hideTitle = false,
    title = 'Recent Usage Trends',
    titleClassName,
    resourceType,
    resourceId,
    csvCategory,
  } = props;

  const [view, setView] = useState<MetricsTrendView>('chart');

  const [layout, setLayout] = useState<RecordsErrorsLayout>('combined');
  const [metricView, setMetricView] = useState<MetricView>(
    hideRecords ? 'volume' : 'recordsErrors',
  );

  const effectiveMetricView: MetricView = hideRecords ? 'volume' : metricView;

  const showLayoutToggle =
    view === 'chart' && !hideRecords && effectiveMetricView === 'recordsErrors';

  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const rows = useMemo(
    () => buildMetricRows({ data, start, end, hideRecords }),
    [data, start, end, hideRecords],
  );

  const sortedRows = useMemo(() => sortMetricRowsByDate(rows, sortDir), [rows, sortDir]);

  const onViewChange = (_event: React.SyntheticEvent, value: MetricsTrendView) => {
    if (value) {
      setView(value);
    }
  };

  const onLayoutChange = (_event: React.SyntheticEvent, value: RecordsErrorsLayout) => {
    if (value) {
      setLayout(value);
    }
  };

  const onMetricViewChange = (_event: React.SyntheticEvent, value: MetricView) => {
    if (value) {
      setMetricView(value);
    }
  };

  const onExportCsv = useCallback(() => {
    downloadMetricsCsv({
      rows: sortedRows,
      start,
      end,
      hideRecords,
      resourceType,
      resourceId,
      category: csvCategory,
    });
  }, [sortedRows, start, end, hideRecords, resourceType, resourceId, csvCategory]);

  const renderToolbarControl = (control: React.ReactNode, skeletonClassName: string) =>
    loading ? (
      <Skeleton animation="wave" variant="rounded" className={skeletonClassName} />
    ) : (
      control
    );

  const renderTrendBody = () => {
    if (loading) return <CenteredLoader height={300} />;
    if (view === 'table')
      return (
        <MetricsTrendTable
          rows={sortedRows}
          hideRecords={hideRecords}
          sortDir={sortDir}
          onSortChange={setSortDir}
        />
      );
    if (effectiveMetricView === 'volume') return <MetricsVolumeChart rows={rows} />;
    if (layout === 'separate') return <MetricsSeparateCharts rows={rows} />;
    return <MetricsCombinedChart rows={rows} hideRecords={hideRecords} />;
  };

  return (
    <Stack className={styles.panel}>
      <header className={styles.header}>
        {!hideTitle && <span className={cx(styles.title, titleClassName)}>{title}</span>}

        {!hideDateControls && (
          <MetricsDateControls start={start} end={end} onDatesChanged={onDatesChanged} />
        )}
      </header>

      <div className={styles.toolbar}>
        {renderToolbarControl(
          <SegmentedControl
            controls={hideRecords ? metricControls.slice(1) : metricControls}
            defaultValue={hideRecords ? 'volume' : 'recordsErrors'}
            value={effectiveMetricView}
            onControlChange={onMetricViewChange}
          />,
          styles.metricControlsSkeleton,
        )}

        <div className={styles.viewControls}>
          {renderToolbarControl(
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={onViewChange}
              className={styles.viewToggle}
            >
              <ToggleButton value="chart" aria-label="Chart view">
                <BarChartOutlined fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <TableChartOutlined fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>,
            styles.viewToggleSkeleton,
          )}

          {showLayoutToggle && !loading && (
            <>
              <Divider orientation="vertical" flexItem className={styles.toolbarDivider} />
              <SegmentedControl
                controls={layoutControls}
                defaultValue="combined"
                value={layout}
                onControlChange={onLayoutChange}
              />
            </>
          )}
        </div>

        <div className={styles.actionGroup}>
          <Tooltip title="Export CSV">
            <span>
              <IconButton
                size="small"
                aria-label="Export CSV"
                onClick={onExportCsv}
                disabled={loading || rows.length === 0}
              >
                <FileDownloadOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className={styles.content}>{renderTrendBody()}</div>
    </Stack>
  );
};

MetricsTrendPanel.displayName = 'MetricsTrendPanel';
